/* eslint-disable consistent-return, default-case */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import Button from '../../components/Inputs/Button/Button';
import './symptoms.css';
import WhatSection from './components/WhatSection';
import WhatToDo from './components/WhatToDo';
import WhenToWorry from './components/WhenToWorry';
import FAQs from './components/FAQs';
import QuickCheck from './components/QuickCheck';
import Congratulations from './components/Congratulations';
import { ADULTSIGNSANDSYMPTOMS } from './signsSymptomsData';

export default function Adult({ signAndSymptomsId }) {
  const [step, setStep] = useState(1);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const totalSteps = 8;
  const progress = (step / totalSteps) * 100;
  const data = ADULTSIGNSANDSYMPTOMS.find((option) => option.id === signAndSymptomsId);

  useEffect(() => {
    const savedProgress = JSON.parse(localStorage.getItem('symptomsProgress') || '{}');
    const currentSymptom = savedProgress[signAndSymptomsId];
    if (currentSymptom?.step) {
      setStep(currentSymptom.step);
    }
  }, [signAndSymptomsId]);

  useEffect(() => {
    const stored = localStorage.getItem('symptomsProgress');
    let savedProgress = {};

    try {
      savedProgress = stored ? JSON.parse(stored) : {};
    } catch {
      savedProgress = {};
    }

    const updatedProgress = {
      ...savedProgress,
      [signAndSymptomsId]: {
        step,
        progress,
      },
    };

    localStorage.setItem('symptomsProgress', JSON.stringify(updatedProgress));
  }, [step, signAndSymptomsId, progress]);

  const getIntroductionAnswer = (introduction) => {
    if (!introduction) return null;

    const answerKeys = Object.keys(introduction).filter((key) => key === 'answer' || /^answer\d+$/.test(key));

    const orderedKeys = answerKeys.sort((a, b) => {
      if (a === 'answer') return -1;
      if (b === 'answer') return 1;
      return Number(a.replace('answer', '')) - Number(b.replace('answer', ''));
    });

    const answers = orderedKeys.map((key) => introduction[key]).filter(Boolean);
    return answers.length <= 1 ? answers[0] : answers;
  };

  const renderContent = (currentStep) => {
    switch (currentStep) {
      case 1:
        return (
          <WhatSection
            question={data.steps.introduction.qstn}
            answer={getIntroductionAnswer(data.steps.introduction)}
          />
        );
      case 2:
        return <WhatToDo title={data.steps.whatToDo.title} options={data.steps.whatToDo.options} />;
      case 3:
        return (
          <WhenToWorry
            title={data.steps.whenToWorry.title}
            disclaimer={data.steps.whenToWorry.title2}
            options={data.steps.whenToWorry.options}
          />
        );
      case 4:
        return (
          <FAQs
            title={data.steps.faqs.title}
            questionTitle={data.steps.faqs.title2}
            questions={data.steps.faqs.qstns}
          />
        );
      case 5:
        return (
          <QuickCheck
            title={data.steps.quickCheck1.title}
            question={data.steps.quickCheck1.qstn}
            answer={data.steps.quickCheck1.answer}
            options={data.steps.quickCheck1.options}
            wrongAnswer={data.steps.quickCheck1.wrongAnswer}
            selectedAnswer={selectedAnswer}
            setSelectedAnswer={setSelectedAnswer}
            answerSubmitted={answerSubmitted}
            setAnswerSubmitted={setAnswerSubmitted}
            isCorrect={isCorrect}
          />
        );
      case 6:
        return (
          <QuickCheck
            title={data.steps.quickCheck2.title}
            question={data.steps.quickCheck2.qstn}
            answer={data.steps.quickCheck2.answer}
            options={data.steps.quickCheck2.options}
            wrongAnswer={data.steps.quickCheck2.wrongAnswer}
            selectedAnswer={selectedAnswer}
            setSelectedAnswer={setSelectedAnswer}
            answerSubmitted={answerSubmitted}
            setAnswerSubmitted={setAnswerSubmitted}
            isCorrect={isCorrect}
          />
        );
      case 7:
        return (
          <QuickCheck
            title={data.steps.quickCheck3.title}
            question={data.steps.quickCheck3.qstn}
            answer={data.steps.quickCheck3.answer}
            options={data.steps.quickCheck3.options}
            wrongAnswer={data.steps.quickCheck3.wrongAnswer}
            selectedAnswer={selectedAnswer}
            setSelectedAnswer={setSelectedAnswer}
            answerSubmitted={answerSubmitted}
            isCorrect={isCorrect}
            setAnswerSubmitted={setAnswerSubmitted}
          />
        );
      case 8:
        return (
          <Congratulations
            title={data.steps.congratulations.title}
            CTA={data.steps.congratulations.CTA}
            path={data.steps.congratulations.path}
          />
        );
    }
  };

  const triggerNextStep = () => {
    if (step < 5 || step === 8) {
      setStep(step + 1);
      setIsCorrect(null);
      return;
    }

    if (!selectedAnswer) return;

    if (!answerSubmitted) {
      const { correct } = selectedAnswer;
      setIsCorrect(correct);
      setAnswerSubmitted(true);
      return;
    }

    if (isCorrect) {
      setStep(step + 1);
      setSelectedAnswer(null);
      setAnswerSubmitted(false);
      setIsCorrect(null);
    }
  };

  const triggerBackStep = () => {
    if (answerSubmitted && isCorrect) {
      setAnswerSubmitted(false);
      setIsCorrect(false);
    } else {
      setStep(step - 1);
    }
  };

  const closePage = () => {
    navigate('/symptoms');
  };

  const resetProgress = () => {
    setStep(1);
    setSelectedAnswer(null);
    setAnswerSubmitted(false);
    setIsCorrect(null);

    const stored = localStorage.getItem('symptomsProgress');
    let savedProgress = {};

    try {
      savedProgress = stored ? JSON.parse(stored) : {};
    } catch {
      savedProgress = {};
    }

    const updatedProgress = {
      ...savedProgress,
      [signAndSymptomsId]: {
        step: 1,
        progress: 0,
      },
    };

    localStorage.setItem('symptomsProgress', JSON.stringify(updatedProgress));
  };

  return (
    <div className="firstaid__container">
      <div className="firstaid__progress">
        <ProgressBar
          width={100}
          percent={progress}
          controls
          onClose={closePage}
          resetProgress={() => resetProgress()}
          resetProgressIcon
        />
      </div>
      <div className="content">
        <div className="symptoms__scroll">{renderContent(step)}</div>
        {step !== 8 && (
          <div className="symptoms__footer">
            {step === 1 ? (
              <div />
            ) : (
              <Button hierarchy="secondary" width="defaultWidth" onClick={() => triggerBackStep(step)}>
                {t('firstAid.backButton')}
              </Button>
            )}
            <Button hierarchy="primary" width="defaultWidth" onClick={() => triggerNextStep(step)}>
              {t('firstAid.nextButton')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
